import React, { useState, useEffect, useCallback } from 'react';
import { api, saveToken, clearToken, parseJwt, refreshTokens, getAuthBase } from './api';
import AddAccountModal from './components/AddAccountModal';
import ComposeModal from './components/ComposeModal';

// ── Helpers ────────────────────────────────────────────────────────────────
function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  const now = new Date();
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function parseFrom(from) {
  const m = from.match(/^"?([^"<]+)"?\s*(?:<[^>]+>)?$/);
  return m ? m[1].trim() : from;
}

function isRead(flags) {
  return flags && flags.includes('\\Seen');
}

const MIN_SIDEBAR_WIDTH = 160;
const MAX_SIDEBAR_WIDTH = 400;
const MIN_MSGLIST_WIDTH = 220;
const MAX_MSGLIST_WIDTH = 560;

const FOLDER_ICONS = {
  INBOX: '📥',
  Sent: '📤',
  Drafts: '📝',
  Trash: '🗑️',
  Spam: '🚫',
  Archive: '📦',
};

// ── Styles ─────────────────────────────────────────────────────────────────
const S = {
  app: { display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'var(--font)' },
  // Sidebar
  sidebar: {
    background: 'var(--bg1)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  sidebarHeader: { padding: '20px 16px 14px', borderBottom: '1px solid var(--line)' },
  logo: { fontFamily: 'var(--mono)', fontSize: 18, color: 'var(--accent)', fontWeight: 500 },
  composeBtn: {
    marginTop: 14,
    width: '100%',
    padding: '9px 0',
    background: 'var(--accent)',
    color: '#fff',
    borderRadius: 'var(--radius)',
    fontWeight: 600,
    fontSize: 13,
  },
  sidebarSection: {
    padding: '14px 8px 6px',
    fontSize: 10,
    color: 'var(--text2)',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    paddingLeft: 14,
  },
  accountItem: {
    padding: '6px 14px',
    cursor: 'pointer',
    borderRadius: 'var(--radius)',
    margin: '0 4px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
  },
  folderItem: {
    padding: '5px 14px 5px 28px',
    cursor: 'pointer',
    borderRadius: 'var(--radius)',
    margin: '0 4px',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  // Message list
  msgList: {
    background: 'var(--bg2)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  resizer: {
    width: 5,
    flexShrink: 0,
    cursor: 'col-resize',
    background: 'var(--line)',
    position: 'relative',
  },
  msgListHeader: {
    padding: '14px 16px',
    borderBottom: '1px solid var(--line)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  msgItem: {
    padding: '12px 14px',
    borderBottom: '1px solid var(--line)',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  msgFrom: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text0)',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  msgSubj: {
    fontSize: 12,
    color: 'var(--text1)',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    marginTop: 2,
  },
  msgDate: { fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' },
  // Reader
  reader: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  readerHeader: { padding: '20px 28px 14px', borderBottom: '1px solid var(--line)' },
  readerSubject: { fontSize: 18, fontWeight: 600, color: 'var(--text0)', marginBottom: 8 },
  readerMeta: { fontSize: 12, color: 'var(--text2)' },
  readerBody: { flex: 1, padding: '24px 28px', overflowY: 'auto', lineHeight: 1.7, fontSize: 14 },
  // Empty
  empty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    color: 'var(--text2)',
    gap: 10,
  },
};

function redirectToLogin() {
  const redirect = encodeURIComponent(window.location.href);
  window.location.href = `${getAuthBase()}/login?redirect=${redirect}`;
}

export default function App() {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState('INBOX');
  const [selectedBoxLabel, setSelectedBoxLabel] = useState('INBOX');
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [msgBody, setMsgBody] = useState(null);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadingBody, setLoadingBody] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(
    () => Number(localStorage.getItem('rm_sidebarWidth')) || 220
  );
  const [msgListWidth, setMsgListWidth] = useState(
    () => Number(localStorage.getItem('rm_msgListWidth')) || 300
  );
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showCompose, setShowCompose] = useState(false);

  // Persist panel widths across reloads
  useEffect(() => {
    localStorage.setItem('rm_sidebarWidth', String(sidebarWidth));
  }, [sidebarWidth]);
  useEffect(() => {
    localStorage.setItem('rm_msgListWidth', String(msgListWidth));
  }, [msgListWidth]);

  // Drag-to-resize for the panel dividers. `offset` is the left edge (in px)
  // of the panel being resized, so the new width is just clientX - offset.
  const startResize = useCallback((setter, min, max, offset) => {
    return e => {
      e.preventDefault();
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      const onMove = moveEvent => {
        setter(Math.min(max, Math.max(min, moveEvent.clientX - offset)));
      };
      const onUp = () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };
  }, []);

  // SSO bootstrap: token from URL → silent refresh → redirect to login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    if (tokenFromUrl) {
      saveToken(tokenFromUrl);
      params.delete('token');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
      const p = parseJwt(tokenFromUrl);
      setUser(p ? { id: p.sub, email: p.email, name: p.name } : null);
      return;
    }
    refreshTokens()
      .then(data => {
        const p = parseJwt(data.accessToken);
        setUser(p ? { id: p.sub, email: p.email, name: p.name } : null);
      })
      .catch(() => {
        setUser(null);
        redirectToLogin();
      });

    const onExpired = () => {
      clearToken();
      setUser(null);
      redirectToLogin();
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  // Load accounts on login
  useEffect(() => {
    if (!user) return;
    api
      .getAccounts()
      .then(acc => {
        setAccounts(acc);
        if (acc.length) setSelectedAccount(acc[0]);
      })
      .catch(() => {});
  }, [user]);

  // Load boxes when account changes
  useEffect(() => {
    if (!selectedAccount) return;
    setBoxes([]);
    setSelectedBox('INBOX');
    setSelectedBoxLabel('INBOX');
    api
      .getBoxes(selectedAccount.id)
      .then(setBoxes)
      .catch(() => {});
  }, [selectedAccount]);

  // Load messages when box/account/page changes
  const loadMessages = useCallback(() => {
    if (!selectedAccount) return;
    setLoadingMsgs(true);
    setLoadError(null);
    setSelectedMsg(null);
    setMsgBody(null);
    api
      .getMessages(selectedAccount.id, selectedBox, page)
      .then(r => {
        setMessages(r.messages || []);
        setTotalMessages(r.total || 0);
      })
      .catch(err => {
        // Don't leave the previous folder's messages on screen when this one fails to load.
        setMessages([]);
        setTotalMessages(0);
        setLoadError(err.message || 'Failed to load messages');
      })
      .finally(() => setLoadingMsgs(false));
  }, [selectedAccount, selectedBox, page]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Load message body
  async function openMessage(msg) {
    setSelectedMsg(msg);
    setMsgBody(null);
    setLoadingBody(true);
    try {
      const body = await api.getMessage(selectedAccount.id, msg.uid, selectedBox);
      setMsgBody(body);
    } catch (e) {
    } finally {
      setLoadingBody(false);
    }
  }

  async function deleteMsg() {
    if (!selectedMsg) return;
    await api.deleteMessage(selectedAccount.id, selectedMsg.uid, selectedBox).catch(() => {});
    setSelectedMsg(null);
    setMsgBody(null);
    loadMessages();
  }

  function logout() {
    fetch(`${getAuthBase()}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(
      () => {}
    );
    clearToken();
    setUser(null);
    setAccounts([]);
    setSelectedAccount(null);
    redirectToLogin();
  }

  if (user === undefined) return null; // loading
  if (!user) return null; // redirecting

  const totalPages = Math.ceil(totalMessages / 30);

  // Determine which boxes to show for selected account.
  // Real IMAP folder names vary by provider (e.g. Gmail's Sent folder is
  // "[Gmail]/Sent Mail", not "Sent"), so the friendly label shown in the
  // sidebar must stay separate from the real name used to select the mailbox.
  const mainBoxes = ['INBOX', 'Sent', 'Drafts', 'Trash', 'Spam', 'Archive'];
  const displayBoxes = boxes.length
    ? mainBoxes
        .map(label => ({
          label,
          realName: boxes.find(bx => bx.name.toLowerCase().includes(label.toLowerCase()))?.name,
        }))
        .filter(b => b.realName)
        .concat(
          boxes
            .filter(bx => !mainBoxes.some(m => bx.name.toLowerCase().includes(m.toLowerCase())))
            .slice(0, 5)
            .map(bx => ({ label: bx.name, realName: bx.name }))
        )
    : mainBoxes.map(label => ({ label, realName: label }));

  return (
    <div style={S.app}>
      {/* ── Sidebar ── */}
      <div style={{ ...S.sidebar, width: sidebarWidth }}>
        <div style={S.sidebarHeader}>
          <div style={S.logo}>⌁ Rainmail</div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
            {user?.name || user?.email}
          </div>
          <button style={S.composeBtn} onClick={() => setShowCompose(true)}>
            ✏ Compose
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {accounts.map(acc => (
            <div key={acc.id}>
              <div
                style={{
                  ...S.sidebarSection,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{acc.label}</span>
              </div>
              {displayBoxes.map(({ label, realName }) => {
                const active = selectedAccount?.id === acc.id && selectedBox === realName;
                return (
                  <div
                    key={realName}
                    onClick={() => {
                      setSelectedAccount(acc);
                      setSelectedBox(realName);
                      setSelectedBoxLabel(label);
                      setPage(1);
                    }}
                    style={{
                      ...S.folderItem,
                      background: active ? 'var(--bg4)' : 'transparent',
                      color: active ? 'var(--text0)' : 'var(--text1)',
                    }}
                  >
                    <span>{FOLDER_ICONS[label] || '📁'}</span>
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{ padding: '12px 8px', borderTop: '1px solid var(--line)' }}>
          <button
            onClick={() => setShowAddAccount(true)}
            style={{
              width: '100%',
              padding: '8px 0',
              background: 'var(--bg3)',
              color: 'var(--text1)',
              borderRadius: 'var(--radius)',
              fontSize: 12,
            }}
          >
            + Add Account
          </button>
          <button
            onClick={logout}
            style={{
              marginTop: 6,
              width: '100%',
              padding: '6px 0',
              color: 'var(--text2)',
              fontSize: 11,
            }}
          >
            Sign Out
          </button>
        </div>
      </div>

      <div
        className="resizer"
        style={S.resizer}
        onMouseDown={startResize(setSidebarWidth, MIN_SIDEBAR_WIDTH, MAX_SIDEBAR_WIDTH, 0)}
      />

      {/* ── Message List ── */}
      <div style={{ ...S.msgList, width: msgListWidth }}>
        <div style={S.msgListHeader}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedBoxLabel}</div>
          <div style={{ fontSize: 11, color: 'var(--text2)' }}>{totalMessages} messages</div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loadingMsgs ? (
            <div style={{ padding: 20, color: 'var(--text2)', fontSize: 12, textAlign: 'center' }}>
              Loading…
            </div>
          ) : loadError ? (
            <div style={{ padding: 20, color: 'var(--red)', fontSize: 12, textAlign: 'center' }}>
              {loadError}
            </div>
          ) : messages.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--text2)', fontSize: 12, textAlign: 'center' }}>
              No messages
            </div>
          ) : (
            messages.map(msg => {
              const active = selectedMsg?.uid === msg.uid;
              const read = isRead(msg.flags);
              return (
                <div
                  key={msg.uid}
                  onClick={() => openMessage(msg)}
                  style={{ ...S.msgItem, background: active ? 'var(--bg4)' : 'transparent' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                    }}
                  >
                    <div style={{ ...S.msgFrom, fontWeight: read ? 400 : 700, maxWidth: 200 }}>
                      {parseFrom(msg.from || '')}
                    </div>
                    <div style={S.msgDate}>{formatDate(msg.date)}</div>
                  </div>
                  <div style={{ ...S.msgSubj, color: read ? 'var(--text2)' : 'var(--text1)' }}>
                    {msg.subject}
                  </div>
                  {!read && (
                    <div
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: 'var(--accent)',
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
        {totalPages > 1 && (
          <div
            style={{
              padding: '10px 14px',
              borderTop: '1px solid var(--line)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ color: page === 1 ? 'var(--text2)' : 'var(--accent)', fontSize: 12 }}
            >
              ← Prev
            </button>
            <span style={{ color: 'var(--text2)', fontSize: 11 }}>
              {page}/{totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                color: page === totalPages ? 'var(--text2)' : 'var(--accent)',
                fontSize: 12,
              }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      <div
        className="resizer"
        style={S.resizer}
        onMouseDown={startResize(
          setMsgListWidth,
          MIN_MSGLIST_WIDTH,
          MAX_MSGLIST_WIDTH,
          sidebarWidth
        )}
      />

      {/* ── Message Reader ── */}
      <div style={S.reader}>
        {!selectedMsg ? (
          accounts.length === 0 ? (
            <div style={S.empty}>
              <div style={{ fontSize: 40 }}>📬</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text1)' }}>
                Welcome to Rainmail
              </div>
              <div style={{ fontSize: 13 }}>Add a mail account to get started</div>
              <button
                onClick={() => setShowAddAccount(true)}
                style={{
                  marginTop: 12,
                  padding: '10px 24px',
                  background: 'var(--accent)',
                  color: '#fff',
                  borderRadius: 'var(--radius)',
                  fontWeight: 600,
                }}
              >
                Add Account
              </button>
            </div>
          ) : (
            <div style={S.empty}>
              <div style={{ fontSize: 36 }}>✉️</div>
              <div style={{ color: 'var(--text1)' }}>Select a message to read</div>
            </div>
          )
        ) : (
          <>
            <div style={S.readerHeader}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1, marginRight: 16 }}>
                  <div style={S.readerSubject}>{selectedMsg.subject || '(no subject)'}</div>
                  <div style={S.readerMeta}>
                    <span style={{ color: 'var(--text1)' }}>From: {selectedMsg.from}</span>
                    <span style={{ margin: '0 12px', color: 'var(--line)' }}>|</span>
                    <span>{formatDate(selectedMsg.date)}</span>
                  </div>
                </div>
                <button
                  onClick={deleteMsg}
                  title="Delete"
                  style={{ color: 'var(--red)', fontSize: 18, padding: '4px 8px' }}
                >
                  🗑
                </button>
              </div>
            </div>
            <div style={S.readerBody}>
              {loadingBody ? (
                <div style={{ color: 'var(--text2)' }}>Loading message…</div>
              ) : msgBody ? (
                msgBody.html ? (
                  <iframe
                    title="message"
                    srcDoc={`<style>body{font-family:sans-serif;font-size:14px;color:#ccc;background:#0b0d14;padding:0;margin:0}a{color:#4f8ef7}img{max-width:100%}</style>${msgBody.html}`}
                    style={{ width: '100%', height: '100%', border: 'none', minHeight: 400 }}
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <pre
                    style={{
                      fontFamily: 'var(--font)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: 'var(--text1)',
                    }}
                  >
                    {msgBody.text || '(empty message)'}
                  </pre>
                )
              ) : null}
            </div>
          </>
        )}
      </div>

      {/* ── Modals ── */}
      {showAddAccount && (
        <AddAccountModal
          onAdd={acc => {
            setAccounts(p => [...p, acc]);
            setSelectedAccount(acc);
            setShowAddAccount(false);
          }}
          onClose={() => setShowAddAccount(false)}
        />
      )}
      {showCompose && accounts.length > 0 && (
        <ComposeModal accounts={accounts} onClose={() => setShowCompose(false)} />
      )}
    </div>
  );
}
